'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import {
  requestPasswordResetOtp,
  verifyPasswordResetOtp,
  resetPasswordWithToken,
  AuthApiError,
} from '@/services/auth.service';
import { HRM_PRODUCT_CODE } from '@/lib/api/config';

const OTP_TTL_SECONDS = 120;
const RESET_TOKEN_TTL_SECONDS = 15 * 60;

type ForgotStep = 'email' | 'otp' | 'new-password' | 'success';

interface ForgotPasswordFlowProps {
  orgCode: string;
  organizationLabel: string;
  initialEmail?: string;
  onBackToLogin: () => void;
  onBackToDomain: () => void;
}

export default function ForgotPasswordFlow({
  orgCode,
  organizationLabel,
  initialEmail = '',
  onBackToLogin,
  onBackToDomain,
}: ForgotPasswordFlowProps) {
  const [step, setStep] = useState<ForgotStep>('email');
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetTokenExpiresIn, setResetTokenExpiresIn] = useState(RESET_TOKEN_TTL_SECONDS);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const gradientBtn =
    'w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)] hover:from-[var(--hrm-primary-dark)] hover:to-[var(--hrm-primary-dark)] text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-[var(--hrm-primary)]/25 hover:shadow-[var(--hrm-primary)]/40 disabled:opacity-60 disabled:cursor-not-allowed';

  const handleSendOtp = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!orgCode.trim()) {
      setError('Organization domain is required. Go back and verify your domain first.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await requestPasswordResetOtp({ email: trimmed, orgCode: orgCode.trim() });
      setResendCooldown(OTP_TTL_SECONDS);
      setStep('otp');
      toast.success('Verification code sent', {
        description: 'Check your inbox for the 6-digit code (expires in 2 minutes).',
      });
    } catch (err) {
      const message =
        err instanceof AuthApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to send verification code';
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, orgCode]);

  const handleResendOtp = useCallback(async () => {
    if (resendCooldown > 0 || isSubmitting) return;
    await handleSendOtp();
  }, [resendCooldown, isSubmitting, handleSendOtp]);

  const handleVerifyOtpContinue = useCallback(async () => {
    if (otp.length !== 6) {
      setError('Enter the 6-digit verification code');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const result = await verifyPasswordResetOtp({
        email: email.trim(),
        orgCode: orgCode.trim(),
        otp: otp.trim(),
      });
      setResetToken(result.resetToken);
      setResetTokenExpiresIn(result.expiresIn ?? RESET_TOKEN_TTL_SECONDS);
      setStep('new-password');
      toast.success('Code verified', {
        description: 'You have 15 minutes to choose a new password (or skip and sign in later).',
      });
    } catch (err) {
      const message =
        err instanceof AuthApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Invalid or expired verification code';
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [otp, email, orgCode]);

  const handleResetPassword = useCallback(async () => {
    if (!resetToken) {
      setError('Session expired. Go back and verify the code again.');
      setStep('otp');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await resetPasswordWithToken({
        resetToken,
        newPassword,
      });
      setStep('success');
      toast.success('Password updated', {
        description: 'You can now sign in with your new password.',
      });
    } catch (err) {
      const message =
        err instanceof AuthApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to reset password';
      setError(message);
      toast.error(message);
      if (message.toLowerCase().includes('expired') || message.toLowerCase().includes('invalid')) {
        setResetToken('');
        setStep('otp');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [newPassword, confirmPassword, resetToken]);

  const stepTitle =
    step === 'email'
      ? 'Forgot Password'
      : step === 'otp'
        ? 'Enter Verification Code'
        : step === 'new-password'
          ? 'Set New Password'
          : 'Password Updated';

  const stepSubtitle =
    step === 'email'
      ? 'Enter your work email. Use the same address you sign in with (must be synced to auth via Sync product users).'
      : step === 'otp'
        ? `We sent a 6-digit code to ${email.trim()}. It expires in 2 minutes.`
        : step === 'new-password'
          ? `Choose a strong password. Your verification is valid for ${Math.ceil(resetTokenExpiresIn / 60)} more minutes.`
          : 'Your password has been reset successfully.';

  return (
    <div className="min-h-screen flex bg-white dark:bg-slate-950">
      {/* Left panel — matches login credentials theme */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-gradient-to-br from-[var(--hrm-primary)] via-[var(--hrm-primary-dark)] to-[var(--hrm-primary-dark)]">
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
            }}
          />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={step === 'success' ? onBackToLogin : onBackToLogin}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl text-white transition-colors"
            >
              <i className="ri-arrow-left-line" />
              <span className="text-sm font-medium">Back to sign in</span>
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="w-64 h-64 bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20 flex flex-col items-center justify-center gap-4 p-8 text-center">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center">
                <i className="ri-lock-password-line text-4xl text-white" />
              </div>
              <p className="text-white/90 text-lg font-semibold">Secure Password Reset</p>
              <p className="text-white/70 text-sm">
                Verify your identity with a one-time code sent to your email.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
              <i className="ri-global-line text-white/80" />
              <span className="text-white/90 text-sm font-medium">{organizationLabel}</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 ml-2 bg-white/10 backdrop-blur-sm rounded-full">
              <i className="ri-apps-line text-white/80 text-sm" />
              <span className="text-white/80 text-xs font-medium">{HRM_PRODUCT_CODE} Product</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — step forms */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12 bg-slate-50 dark:bg-slate-950">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-right-4 duration-500">
          <div className="lg:hidden mb-6">
            <button
              type="button"
              onClick={onBackToLogin}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 mb-4"
            >
              <i className="ri-arrow-left-line" />
              Back to sign in
            </button>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-8 border border-slate-100 dark:border-slate-800">
            {/* Step indicator */}
            {step !== 'success' && (
              <div className="flex items-center justify-center gap-2 mb-6">
                {(['email', 'otp', 'new-password'] as const).map((s, idx) => {
                  const order = { email: 0, otp: 1, 'new-password': 2 };
                  const current = order[step === 'new-password' ? 'new-password' : step];
                  const active = order[s] <= current;
                  return (
                    <React.Fragment key={s}>
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                          active
                            ? 'bg-[var(--hrm-primary-dark)] text-white'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                        }`}
                      >
                        {idx + 1}
                      </div>
                      {idx < 2 && (
                        <div
                          className={`h-0.5 w-8 ${order[s] < current ? 'bg-[var(--hrm-primary-dark)]' : 'bg-slate-200 dark:bg-slate-700'}`}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[var(--hrm-primary-light)] dark:bg-[var(--hrm-primary)]/10 rounded-xl mb-4">
                <i
                  className={`text-2xl text-[var(--hrm-primary)] ${
                    step === 'success'
                      ? 'ri-checkbox-circle-line'
                      : step === 'otp'
                        ? 'ri-mail-check-line'
                        : 'ri-lock-unlock-line'
                  }`}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">{stepTitle}</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">{stepSubtitle}</p>
            </div>

            {/* Org + product context */}
            {step !== 'success' && (
              <div className="flex flex-wrap items-center gap-2 mb-5 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                  <i className="ri-building-line text-[var(--hrm-primary-dark)]" />
                  {organizationLabel}
                </span>
                <span className="text-slate-300 dark:text-slate-600">·</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                  <i className="ri-apps-line text-[var(--hrm-primary-dark)]" />
                  {HRM_PRODUCT_CODE}
                </span>
              </div>
            )}

            {error && (
              <p className="mb-4 text-sm text-red-500 flex items-center gap-1.5">
                <i className="ri-error-warning-line" />
                {error}
              </p>
            )}

            {/* Step 1: Email */}
            {step === 'email' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleSendOtp();
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Work Email
                  </label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError('');
                      }}
                      placeholder="you@company.com"
                      required
                      autoComplete="email"
                      disabled={isSubmitting}
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--hrm-primary)]/20 focus:border-[var(--hrm-primary)] transition-all disabled:opacity-60"
                    />
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting} className={gradientBtn}>
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending code...
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" />
                      Send verification code
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onBackToDomain}
                  className="w-full text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Change organization domain
                </button>
              </form>
            )}

            {/* Step 2: OTP */}
            {step === 'otp' && (
              <div className="space-y-5">
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={(value) => {
                      setOtp(value.replace(/\D/g, '').slice(0, 6));
                      setError('');
                    }}
                    disabled={isSubmitting}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="h-12 w-11 text-lg" />
                      <InputOTPSlot index={1} className="h-12 w-11 text-lg" />
                      <InputOTPSlot index={2} className="h-12 w-11 text-lg" />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} className="h-12 w-11 text-lg" />
                      <InputOTPSlot index={4} className="h-12 w-11 text-lg" />
                      <InputOTPSlot index={5} className="h-12 w-11 text-lg" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="text-center text-xs text-slate-400">
                  {resendCooldown > 0 ? (
                    <>Resend available in {resendCooldown}s</>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleResendOtp()}
                      disabled={isSubmitting}
                      className="text-[var(--hrm-primary)] hover:text-[var(--hrm-primary-dark)] font-medium disabled:opacity-50"
                    >
                      Resend code
                    </button>
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => void handleVerifyOtpContinue()}
                  disabled={otp.length !== 6 || isSubmitting}
                  className={gradientBtn}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      Verify code
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setOtp('');
                    setError('');
                  }}
                  className="w-full text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Use a different email
                </button>
              </div>
            )}

            {/* Step 3: New password */}
            {step === 'new-password' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleResetPassword();
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <i className="ri-lock-password-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setError('');
                      }}
                      placeholder="At least 8 characters"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      disabled={isSubmitting}
                      className="w-full pl-11 pr-12 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--hrm-primary)]/20 focus:border-[var(--hrm-primary)] transition-all disabled:opacity-60"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <i className={showPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'} />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <i className="ri-lock-password-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setError('');
                      }}
                      placeholder="Re-enter your password"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      disabled={isSubmitting}
                      className="w-full pl-11 pr-12 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--hrm-primary)]/20 focus:border-[var(--hrm-primary)] transition-all disabled:opacity-60"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      <i
                        className={
                          showConfirmPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'
                        }
                      />
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting || !resetToken} className={gradientBtn}>
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Updating password...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line" />
                      Reset password
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('otp');
                    setResetToken('');
                    setError('');
                  }}
                  className="w-full text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Back to verification code
                </button>
                <button
                  type="button"
                  onClick={onBackToLogin}
                  className="w-full text-sm text-slate-500 hover:text-[var(--hrm-primary-dark)] dark:hover:text-[var(--hrm-primary)]"
                >
                  Skip for now — return to sign in
                </button>
              </form>
            )}

            {/* Step 4: Success */}
            {step === 'success' && (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 mx-auto bg-emerald-50 dark:bg-emerald-900/20 rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-3xl text-emerald-500" />
                </div>
                <button type="button" onClick={onBackToLogin} className={gradientBtn}>
                  <i className="ri-login-box-line" />
                  Sign in with new password
                </button>
              </div>
            )}
          </div>

          {import.meta.env.DEV && step === 'otp' && (
            <p className="mt-4 text-xs text-center text-slate-400">
              Dev tip: OTP is logged in the auth-service terminal. For @iqsetters.yopmail.com addresses,
              open https://iqsetters.yopmail.com and enter your inbox name.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
